<template>
  <div v-if="visible" class="bonded-dialog-mask" @click.self="close">
    <TanchuangFrame title="综保区" v-slot="{ backgroundStyle }">
      <section class="bonded-dialog" :style="backgroundStyle">
        <div class="bonded-dialog__tools">
          <div class="bonded-tool-actions">
            <button type="button" class="bonded-tool-btn" @click="openStatisticsDialog">导出</button>
            <button type="button" class="bonded-tool-btn" @click="close">关闭</button>
          </div>
          <div class="bonded-period-switch">
            <button
                v-for="(item, index) in periodTabs"
                :key="item"
                type="button"
                :class="{ active: index === periodIndex }"
                @click="switchPeriod(index)"
            >{{ item }}
            </button>
          </div>
        </div>

        <div class="bonded-dialog__grid">
          <article class="bonded-chart-card bonded-chart-card--primary">
            <TabHeader
                class="bonded-chart-header bonded-chart-header--primary"
                :items="topTabs"
                :active-index="topTabIndex"
                title-mode="switch"
                @change="switchTopTab"
            />
            <div class="bonded-unit">{{ topUnit }}</div>
            <div ref="topChart" class="bonded-chart-box"></div>
          </article>

          <article class="bonded-chart-card bonded-chart-card--tir">
            <TabHeader :items="['保税+TIR']" :active-index="0"/>
            <div class="bonded-unit">辆/吨/万元</div>
            <div ref="tirChart" class="bonded-chart-box"></div>
          </article>

          <article class="bonded-chart-card bonded-chart-card--repair">
            <TabHeader :items="['保税维修']" :active-index="0"/>
            <div class="bonded-repair-table-wrap">
              <table class="bonded-repair-table">
                <colgroup>
                  <col class="repair-col-date">
                  <col class="repair-col-name">
                  <col class="repair-col-count">
                  <col class="repair-col-value">
                </colgroup>
                <thead>
                <tr>
                  <th>日期</th>
                  <th>入区待维修货物品名</th>
                  <th>数量（台/件）</th>
                  <th>货值（万元）</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in repairRows" :key="item.id || `${item.statDate}-${item.goodsName}`"
                    @click="selectedRepair = item">
                  <td>{{ formatDate(item.statDate) }}</td>
                  <td class="repair-name-cell">{{ item.goodsName || '未填写品名' }}</td>
                  <td>{{ item.quantity || 0 }}</td>
                  <td>{{ item.tradeValue || 0 }}</td>
                </tr>
                <tr v-if="!repairRows.length">
                  <td colspan="4" class="bonded-empty">暂无数据</td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-if="repairTotalPages > 1" class="bonded-repair-pagination">
              <button class="bonded-page-nav" type="button" :disabled="repairPageNum <= 1"
                      @click="changeRepairPage(repairPageNum - 1)">上一页
              </button>
              <button
                  v-for="item in repairPageItems"
                  :key="item.key"
                  type="button"
                  class="bonded-page-number"
                  :class="{ active: item.page === repairPageNum, 'is-ellipsis': item.type === 'ellipsis' }"
                  :disabled="item.type === 'ellipsis'"
                  @click="item.page && changeRepairPage(item.page)"
              >{{ item.label }}
              </button>
              <button class="bonded-page-nav" type="button" :disabled="repairPageNum >= repairTotalPages"
                      @click="changeRepairPage(repairPageNum + 1)">下一页
              </button>
              <label class="bonded-page-jumper">
                <span>跳至</span>
                <input
                    v-model="repairJumpPage"
                    type="number"
                    min="1"
                    :max="repairTotalPages"
                    step="1"
                    aria-label="跳转页码"
                    @keyup.enter="jumpRepairPage"
                >
                <span>页</span>
              </label>
              <button class="bonded-page-go" type="button" @click="jumpRepairPage">跳转</button>
            </div>
          </article>

          <article class="bonded-chart-card bonded-chart-card--railway">
            <TabHeader :items="['铁路+跨境电商（9610）']" :active-index="0"/>
            <div class="bonded-unit">个/票/万元</div>
            <div ref="railwayChart" class="bonded-chart-box bonded-chart-box--railway"></div>
          </article>

          <article class="bonded-chart-card bonded-chart-card--precheck">
            <TabHeader :items="['先查后装']" :active-index="0"/>
            <div class="bonded-unit">票/辆</div>
            <div ref="precheckChart" class="bonded-chart-box"></div>
          </article>
        </div>
      </section>
    </TanchuangFrame>

    <StatisticsDialog
        :visible="showStatisticsDialog"
        source="bonded"
        :metric-options="statisticsMetricOptions"
        :default-metric="statisticsMetricOptions[0]"
        @close="closeStatisticsDialog"
    />

    <div v-if="selectedRepair" class="repair-detail-mask" @click.self="selectedRepair = null">
      <div class="repair-detail">
        <div class="repair-detail__head"><strong>保税维修明细</strong>
          <button type="button" @click="selectedRepair = null">×</button>
        </div>
        <div class="repair-detail__grid"><span>日期</span><strong>{{
            formatDate(selectedRepair.statDate)
          }}</strong><span>货物品名</span><strong>{{
            selectedRepair.goodsName
          }}</strong><span>数量（台/件）</span><strong>{{
            selectedRepair.quantity || 0
          }}</strong><span>货值（万元）</span><strong>{{ selectedRepair.tradeValue || 0 }}</strong></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {getBondedOverview} from '@/api/datav'
import StatisticsDialog from '@/components/common/StatisticsDialog.vue'
import TabHeader from '@/components/common/TabHeader.vue'
import TanchuangFrame from '@/components/common/TanchuangFrame.vue'

export default {
  name: 'BondedRegionDialog',
  components: {TabHeader, StatisticsDialog, TanchuangFrame},
  props: {
    visible: {type: Boolean, default: false},
    periodKey: {type: String, default: 'day'},
    dateRange: {type: Object, default: () => ({})}
  },
  data() {
    return {
      localPeriodKey: this.normalizePeriod(this.periodKey),
      periodTabs: ['日数据', '月数据', '年数据'],
      topTabs: ['区外加工贸易', '出口商品车'],
      topTabIndex: 0,
      overview: {},
      charts: [],
      topChart: null,
      tirChart: null,
      railwayChart: null,
      precheckChart: null,
      selectedRepair: null,
      repairPageNum: 1,
      repairPageSize: 10,
      repairJumpPage: '',
      showStatisticsDialog: false,
      statisticsMetricOptions: ['区外加工贸易', '出口商品车', '保税+TIR', '保税维修', '铁路+跨境电商（9610）', '先查后装']
    }
  },
  computed: {
    periodIndex() {
      return ['day', 'month', 'year'].indexOf(this.localPeriodKey)
    },
    topData() {
      return this.topTabIndex === 0 ? this.overview.outsideTrade : this.overview.exportVehicle
    },
    topUnit() {
      return this.topTabIndex === 0 ? '万元/%' : '台/亿元'
    },
    repairRows() {
      return Array.isArray(this.overview.repair) ? this.overview.repair : []
    },
    repairTotal() {
      return Number(this.overview.repairTotal) || 0
    },
    repairTotalPages() {
      return Math.max(1, Math.ceil(this.repairTotal / this.repairPageSize))
    },
    repairPageItems() {
      const total = this.repairTotalPages
      const toPageItem = page => ({key: `page-${page}`, type: 'page', page, label: String(page)})

      if (total <= 7) {
        return Array.from({length: total}, (_, index) => toPageItem(index + 1))
      }

      const current = this.repairPageNum
      let start = Math.max(2, current - 1)
      let end = Math.min(total - 1, current + 1)

      if (current <= 3) end = 4
      if (current >= total - 2) start = total - 3

      const items = [toPageItem(1)]
      if (start > 2) items.push({key: 'ellipsis-left', type: 'ellipsis', page: null, label: '...'})
      for (let page = start; page <= end; page += 1) items.push(toPageItem(page))
      if (end < total - 1) items.push({key: 'ellipsis-right', type: 'ellipsis', page: null, label: '...'})
      items.push(toPageItem(total))
      return items
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.localPeriodKey = this.normalizePeriod(this.periodKey);
        this.repairPageNum = 1;
        this.repairJumpPage = '';
        this.open()
      } else {
        this.showStatisticsDialog = false;
        this.disposeCharts();
        this.selectedRepair = null
      }
    },
    periodKey(value) {
      if (!this.visible) this.localPeriodKey = this.normalizePeriod(value)
    },
    dateRange: {
      deep: true, handler() {
        if (this.visible) this.open()
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize);
    if (this.visible) this.open()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize);
    this.disposeCharts()
  },
  methods: {
    normalizePeriod(value) {
      return ['day', 'month', 'year'].includes(value) ? value : 'day'
    },
    close() {
      this.showStatisticsDialog = false;
      this.$emit('close')
    },
    async open() {
      try {
        this.overview = await getBondedOverview(this.localPeriodKey, this.dateRange, {
          repairPageNum: this.repairPageNum,
          repairPageSize: this.repairPageSize
        }) || {}
      } catch (error) {
        console.error('综保区数据加载失败', error)
        this.overview = {}
      }
      this.$nextTick(() => {
        this.initCharts();
        this.renderCharts()
      })
    },
    switchPeriod(index) {
      const value = ['day', 'month', 'year'][index] || 'day';
      if (value === this.localPeriodKey) return;
      this.localPeriodKey = value;
      this.repairPageNum = 1;
      this.repairJumpPage = '';
      this.open()
    },
    switchTopTab(index) {
      this.topTabIndex = index;
      this.$nextTick(() => this.renderTopChart())
    },
    changeRepairPage(pageNum) {
      if (pageNum < 1 || pageNum > this.repairTotalPages || pageNum === this.repairPageNum) return
      this.repairPageNum = pageNum
      this.repairJumpPage = ''
      this.open()
    },
    jumpRepairPage() {
      const inputPage = Number.parseInt(this.repairJumpPage, 10)
      if (!Number.isFinite(inputPage)) return
      const targetPage = Math.min(this.repairTotalPages, Math.max(1, inputPage))
      if (targetPage === this.repairPageNum) {
        this.repairJumpPage = ''
        return
      }
      this.changeRepairPage(targetPage)
    },
    openStatisticsDialog() {
      this.showStatisticsDialog = true
    },
    closeStatisticsDialog() {
      this.showStatisticsDialog = false
    },
    initCharts() {
      if (!this.$refs.topChart || !this.$refs.tirChart || !this.$refs.railwayChart || !this.$refs.precheckChart) return
      if (!this.topChart) this.topChart = echarts.init(this.$refs.topChart)
      if (!this.tirChart) this.tirChart = echarts.init(this.$refs.tirChart)
      if (!this.railwayChart) this.railwayChart = echarts.init(this.$refs.railwayChart)
      if (!this.precheckChart) this.precheckChart = echarts.init(this.$refs.precheckChart)
      this.charts = [this.topChart, this.tirChart, this.railwayChart, this.precheckChart]
    },
    renderCharts() {
      this.renderTopChart();
      this.renderChart(this.tirChart, this.overview.tir);
      this.renderChart(this.railwayChart, this.overview.railway9610);
      this.renderChart(this.precheckChart, this.overview.precheckLoad)
    },
    renderTopChart() {
      this.renderChart(this.topChart, this.topData)
    },
    renderChart(chart, source) {
      if (!chart) return
      const data = source || {}
      const series = Array.isArray(data.series) ? data.series : []
      chart.setOption({
        animationDuration: 420,
        color: ['#35d8ff', '#ffc44f', '#7ff36f', '#ff7f6a'],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(4, 24, 58, .96)',
          borderColor: 'rgba(78, 193, 255, .34)',
          textStyle: {color: '#eaf9ff', fontSize: 11},
          axisPointer: {lineStyle: {color: 'rgba(96, 208, 255, .28)'}}
        },
        legend: {
          top: 14,
          right: 10,
          data: data.legend || series.map(item => item.name),
          textStyle: {color: '#cceeff', fontSize: 11, padding: [0, 0, 0, 2]},
          itemWidth: 12,
          itemHeight: 9,
          itemGap: 18
        },
        grid: {left: 38, right: 38, top: 31, bottom: 2, containLabel: true},
        xAxis: {
          type: 'category',
          data: data.labels || [],
          axisLine: {lineStyle: {color: 'rgba(112, 184, 224, .42)'}},
          axisTick: {show: false},
          axisLabel: {color: '#a9d5ec', fontSize: 10, margin: 4}
        },
        yAxis: [
          {
            type: 'value',
            position: 'left',
            axisLine: {show: false},
            axisTick: {show: false},
            axisLabel: {color: '#a9d5ec', fontSize: 10},
            splitLine: {lineStyle: {color: 'rgba(113, 191, 235, .14)'}}
          },
          {
            type: 'value',
            position: 'right',
            axisLine: {show: false},
            axisTick: {show: false},
            axisLabel: {show: true, color: '#a9d5ec', fontSize: 10},
            splitLine: {show: false}
          }
        ],
        series: series.map(item => ({
          name: item.name,
          type: item.type || 'bar',
          data: item.data || [],
          yAxisIndex: item.type === 'line' ? 1 : 0,
          smooth: item.type === 'line' ? 0.35 : false,
          showSymbol: item.type === 'line' ? false : true,
          barMaxWidth: 18,
          itemStyle: {borderRadius: item.type === 'bar' ? [3, 3, 0, 0] : 0}
        }))
      }, true)
    },
    resize() {
      this.charts.forEach(chart => chart && chart.resize())
    },
    disposeCharts() {
      this.charts.forEach(chart => chart && chart.dispose());
      this.charts = [];
      this.topChart = null;
      this.tirChart = null;
      this.railwayChart = null;
      this.precheckChart = null
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : '-'
    }
  }
}
</script>

<style scoped>
.bonded-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 13, 36, .62);
}

.bonded-dialog-mask :deep(.stats-dialog-layer) {
  position: fixed;
  z-index: 120;
}

.bonded-dialog {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: clamp(92px, 5.1vw, 116px) clamp(48px, 2.7vw, 66px) clamp(24px, 2.4vh, 34px);
  box-sizing: border-box;
  overflow: hidden;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.bonded-dialog__tools {
  position: absolute;
  top: clamp(30px, 3vh, 42px);
  right: clamp(40px, 3.5vw, 64px);
  z-index: 3;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: clamp(6px, .5vh, 10px);
}

.bonded-tool-actions {
  display: inline-flex;
  align-items: center;
  gap: .42vw;
}

.bonded-tool-btn {
  height: clamp(22px, 2.2vh, 28px);
  padding: 0 .78vw;
  border: 1px solid rgba(97, 205, 255, .42);
  border-radius: .28vw;
  color: #d9f6ff;
  font-size: clamp(12px, .58vw, 14px);
  font-weight: 700;
  cursor: pointer;
  background: rgba(16, 79, 167, .5);
  box-shadow: inset 0 0 .55vw rgba(99, 210, 255, .13);
}

.bonded-period-switch {
  display: inline-grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .12vw;
  width: clamp(150px, 10.2vw, 198px);
  padding: .16vh .18vw;
  border: 1px solid rgba(71, 186, 255, .34);
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(11, 78, 164, .7), rgba(5, 38, 98, .52)), rgba(3, 21, 60, .58);
  box-shadow: inset 0 0 .7vw rgba(33, 177, 255, .12);
}

.bonded-period-switch button {
  height: clamp(20px, 2vh, 26px);
  border: 0;
  border-radius: 999vw;
  color: rgba(220, 245, 255, .82);
  font-size: clamp(12px, .58vw, 14px);
  font-weight: 700;
  cursor: pointer;
  background: transparent;
}

.bonded-period-switch button.active {
  color: #f6feff;
  background: linear-gradient(180deg, rgba(21, 210, 255, .44), rgba(14, 108, 235, .2)), linear-gradient(180deg, rgba(13, 91, 211, .98), rgba(8, 62, 158, .94));
  box-shadow: inset 0 0 0 1px rgba(119, 241, 255, .5), 0 0 .82vw rgba(32, 180, 255, .26);
}

.bonded-dialog__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) clamp(205px, 22vh, 230px);
  column-gap: clamp(20px, 2vw, 46px);
  row-gap: clamp(4px, .6vh, 8px);
  width: 100%;
  height: 100%;
  min-height: 0;
}

.bonded-chart-card {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 1.85vh .45vw .1vh;
}

.bonded-chart-card--primary {
  grid-column: 1;
  grid-row: 1;
}

.bonded-chart-card--tir {
  grid-column: 2;
  grid-row: 1;
}

.bonded-chart-card--precheck {
  grid-column: 1;
  grid-row: 2;
}

.bonded-chart-card--railway {
  grid-column: 2;
  grid-row: 2;
}

.bonded-chart-card--repair {
  grid-column: 1 / -1;
  grid-row: 3;
  padding-top: clamp(34px, 3.4vh, 40px);
}

.bonded-chart-header {
  position: absolute;
  top: 0;
  left: .2vw;
  right: .2vw;
  z-index: 2;
}

.bonded-chart-card > :deep(.tab-header) {
  position: absolute;
  top: 0;
  left: .2vw;
  right: .2vw;
  z-index: 6;
  align-items: center;
  height: clamp(26px, 2.9vh, 34px);
  padding-left: 2.5vw;
  padding-right: .2vw;
  margin-bottom: 0;
}

.bonded-chart-card--repair > :deep(.tab-header) {
  padding-left: 3.35vw;
}

.bonded-chart-header {
  position: absolute;
  top: 0;
  left: .2vw;
  right: .2vw;
  z-index: 2;
}

.bonded-chart-card :deep(.tab-header--switch .tab-titles--switch) {
  width: fit-content;
  min-width: 0;
  padding: .08vw;
  border-radius: .48vw;
}

.bonded-chart-card :deep(.tab-header--switch .tab-text--switch) {
  min-width: 4.9vw;
  height: clamp(19px, 2vh, 23px);
  padding: 0 .72vw;
  font-size: .68vw;
  font-weight: 700;
  border-radius: .32vw;
}

.bonded-chart-card :deep(.tab-header .tab-text) {
  font-size: .84vw;
  font-weight: 800;
  letter-spacing: .02vw;
}

.bonded-chart-card :deep(.tab-header .tab-text.active) {
  text-shadow: 0 0 .8vw rgba(64, 194, 255, .42);
}

.bonded-unit {
  position: absolute;
  top: 3.25vh;
  left: .55vw;
  z-index: 2;
  color: #cceeff;
  font-size: clamp(11px, .68vw, 14px);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}

.bonded-chart-box {
  flex: 1 1 0;
  width: 100%;
  min-height: 0;
  height: 100%;
}

.bonded-chart-box--railway {
  flex-basis: 0;
  height: auto;
}

.bonded-repair-table-wrap {
  position: relative;
  z-index: 1;
  flex: 0 0 132px;
  width: 100%;
  height: 132px;
  min-height: 132px;
  max-height: 132px;
  padding: .35vh .14vw .12vh 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-color: rgba(92, 204, 255, .56) rgba(12, 49, 110, .18);
  scrollbar-width: thin;
}

.bonded-repair-table-wrap::-webkit-scrollbar {
  width: .3vw;
  height: .3vw;
}

.bonded-repair-table-wrap::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(55, 214, 255, .92), rgba(16, 138, 255, .86));
  box-shadow: 0 0 .4vw rgba(50, 195, 255, .22);
}

.bonded-repair-table-wrap::-webkit-scrollbar-track {
  border-radius: 999px;
  background: rgba(12, 49, 110, .18);
}

.bonded-repair-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 5px;
  table-layout: fixed;
}

.bonded-repair-table thead {
  position: sticky;
  top: 0;
  z-index: 4;
}

.bonded-repair-table th {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 27px;
  padding: 0 5px;
  color: #d6f2ff;
  font-size: .68vw;
  font-weight: 700;
  background: linear-gradient(180deg, rgba(30, 113, 211, .98), rgba(8, 60, 139, .98));
}

.bonded-repair-table td {
  height: 28px;
  padding: 0 5px;
  border-top: 1px solid rgba(75, 183, 255, .2);
  border-bottom: 1px solid rgba(75, 183, 255, .2);
  color: #e6f8ff;
  font-size: .64vw;
  text-align: center;
  background: rgba(6, 48, 105, .48);
  cursor: pointer;
}

.bonded-repair-table tr td:first-child {
  border-left: 1px solid rgba(75, 183, 255, .2);
}

.bonded-repair-table tr td:last-child {
  border-right: 1px solid rgba(75, 183, 255, .2);
}

.bonded-repair-table tbody tr:hover td {
  color: #fff;
  background: rgba(18, 105, 179, .7);
}

.repair-col-date {
  width: 18%;
}

.repair-col-name {
  width: 38%;
}

.repair-col-count {
  width: 22%;
}

.repair-col-value {
  width: 22%;
}

.repair-name-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bonded-empty {
  height: 80px;
  color: rgba(210, 238, 255, .66) !important;
  text-align: center;
}

.bonded-repair-pagination {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  align-self: flex-end;
  gap: .42vw;
  margin-top: auto;
  min-height: 1.85vw;
  padding-right: .12vw;
  color: #bfe8ff;
  font-size: .62vw;
}

.bonded-repair-pagination button {
  height: 1.7vw;
  min-height: 24px;
  border: 1px solid rgba(97, 205, 255, .35);
  border-radius: .28vw;
  color: #d9f6ff;
  font-size: .62vw;
  cursor: pointer;
  background: rgba(16, 79, 167, .5);
}

.bonded-page-nav {
  min-width: 3.2vw;
  padding: 0 .4vw;
}

.bonded-page-number {
  width: 1.7vw;
  min-width: 24px;
  padding: 0;
}

.bonded-page-number.active {
  border-color: rgba(119, 241, 255, .72);
  color: #ffffff;
  background: linear-gradient(180deg, rgba(26, 177, 255, .82), rgba(8, 86, 190, .88));
  box-shadow: inset 0 0 0 1px rgba(128, 238, 255, .22), 0 0 .55vw rgba(41, 184, 255, .2);
}

.bonded-page-number.is-ellipsis {
  border-color: transparent;
  color: rgba(191, 232, 255, .78);
  background: transparent;
  opacity: 1;
}

.bonded-page-jumper {
  display: inline-flex;
  align-items: center;
  gap: .22vw;
  margin-left: .18vw;
  color: rgba(207, 240, 255, .88);
  white-space: nowrap;
}

.bonded-page-jumper input {
  width: 2.5vw;
  min-width: 42px;
  height: 1.7vw;
  min-height: 24px;
  padding: 0 .3vw;
  border: 1px solid rgba(97, 205, 255, .42);
  border-radius: .28vw;
  box-sizing: border-box;
  outline: none;
  color: #effcff;
  font-size: .62vw;
  text-align: center;
  background: rgba(5, 48, 112, .72);
  box-shadow: inset 0 0 .45vw rgba(78, 205, 255, .1);
}

.bonded-page-jumper input:focus {
  border-color: rgba(119, 241, 255, .8);
  box-shadow: inset 0 0 .45vw rgba(78, 205, 255, .14), 0 0 .45vw rgba(41, 184, 255, .18);
}

.bonded-page-go {
  min-width: 2.9vw;
  padding: 0 .42vw;
}

.bonded-repair-pagination button:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.repair-detail-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 12, 30, .66);
}

.repair-detail {
  width: min(420px, calc(100vw - 36px));
  border: 1px solid rgba(93, 213, 255, .5);
  background: linear-gradient(180deg, #082c58, #041832);
  box-shadow: 0 0 24px rgba(28, 152, 220, .25);
}

.repair-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  border-bottom: 1px solid rgba(101, 201, 255, .2);
  color: #ecfbff;
}

.repair-detail__head button {
  border: 0;
  color: #bcecff;
  font-size: 20px;
  cursor: pointer;
  background: transparent;
}

.repair-detail__grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px 14px;
  padding: 18px 15px 20px;
  font-size: 13px;
}

.repair-detail__grid span {
  color: #8eb8d4;
}

.repair-detail__grid strong {
  color: #f1fdff;
  font-weight: 600;
}

@media (max-width: 900px) {
  .bonded-dialog {
    padding-left: 28px;
    padding-right: 28px;
  }

  .bonded-dialog__grid {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(5, minmax(170px, 1fr));
    overflow: auto;
  }

  .bonded-chart-card {
    min-height: 170px;
    grid-column: auto;
    grid-row: auto;
  }

  .bonded-chart-card--primary {
    order: 1;
  }

  .bonded-chart-card--tir {
    order: 2;
  }

  .bonded-chart-card--precheck {
    order: 3;
  }

  .bonded-chart-card--railway {
    order: 4;
  }

  .bonded-chart-card--repair {
    order: 5;
  }
}
</style>
